import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import ConferenceGrid from './components/ConferenceGrid';
import MobileFilterDrawer from './components/MobileFilterDrawer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ActiveFilters from '../../components/ui/ActiveFilters';
import { conferenceService } from '../../services/conferenceService';

const PublicConferenceBoard = () => {
  const { t } = useTranslation('conferences');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('relevance');
  const [filteredConferences, setFilteredConferences] = useState(null);
  const [allConferences, setAllConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [error, setError] = useState(null);
  const PAGE_SIZE = 9;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [showArchive, setShowArchive] = useState(false);

  // Fetch conferences from Supabase
  useEffect(() => {
    const fetchConferences = async () => {
      try {
        setLoading(true);
        setError(null);
        const conferences = await conferenceService?.getAllPublished();
        const bannerUrlMap = await conferenceService?.getBannerImageUrls(
          conferences?.map(conf => conf?.bannerImageUrl)
        );
        const conferencesWithBannerUrls = conferences?.map((conference) => ({
          ...conference,
          bannerUrl: conference?.bannerImageUrl ? bannerUrlMap?.get(conference?.bannerImageUrl) || null : null
        }));

        setAllConferences(conferencesWithBannerUrls);
        setFilteredConferences(conferencesWithBannerUrls);
      } catch (err) {
        console.error('Failed to fetch conferences:', err);
        setError(err?.message || 'Failed to load conferences');
        setFilteredConferences([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConferences();
  }, []);

  // A conference is "archived" once its dates have passed and the owner hasn't
  // moved them into the future. Purely a view filter — no data is deleted; all
  // papers, organizers and review results remain accessible to organizers.
  const isPastConference = (conf) => {
    const ref = conf?.endDate || conf?.startDate;
    if (!ref) return false; // dates TBA → treat as active
    const d = new Date(ref);
    if (Number.isNaN(d?.getTime())) return false;
    d?.setHours(23, 59, 59, 999);
    return d?.getTime() < Date.now();
  };

  /* ------------------------------------------------------------------------
     One predicate per dimension.
     Pulled out of the old single `filterConferences` chain so each can be
     applied independently — which is what makes facet counts possible: to count
     what "Medicine" would return, you apply every filter EXCEPT subject, then
     test subject alone. The old chain could only ever produce the final set.
     ---------------------------------------------------------------------- */
  const matchQuery = useCallback((conf) => {
    const q = searchQuery?.trim()?.toLowerCase();
    if (!q) return true;
    return (
      conf?.name?.toLowerCase()?.includes(q) ||
      conf?.description?.toLowerCase()?.includes(q) ||
      conf?.subjectArea?.toLowerCase()?.includes(q)
    );
  }, [searchQuery]);

  const matchSubject = (conf, list) =>
    !list?.length || list?.some((s) => conf?.subjectArea?.toLowerCase()?.includes(s));
  const matchLocation = (conf, list) =>
    !list?.length || list?.some((l) => conf?.locationType?.toLowerCase()?.includes(l));
  const matchStatus = (conf, list) =>
    !list?.length ||
    list?.some((s) => conf?.status?.toLowerCase()?.replace('_', '-')?.includes(s));

  const matchDates = useCallback((conf) => {
    if (dateRange?.start) {
      if (new Date(conf?.startDate) < new Date(dateRange.start)) return false;
    }
    if (dateRange?.end) {
      if (new Date(conf?.startDate) > new Date(dateRange.end)) return false;
    }
    return true;
  }, [dateRange]);

  const filterConferences = useCallback(() => {
    let filtered = allConferences?.filter(
      (conf) =>
        matchQuery(conf) &&
        matchSubject(conf, selectedSubjects) &&
        matchLocation(conf, selectedLocations) &&
        matchStatus(conf, selectedStatuses) &&
        matchDates(conf)
    ) || [];

    switch (sortBy) {
      case 'deadline':
        filtered?.sort((a, b) =>
        new Date(a?.paperDeadline) - new Date(b?.paperDeadline)
        );
        break;
      case 'date-asc':
        filtered?.sort((a, b) =>
        new Date(a?.startDate) - new Date(b?.startDate)
        );
        break;
      case 'date-desc':
        filtered?.sort((a, b) =>
        new Date(b?.startDate) - new Date(a?.startDate)
        );
        break;
      default:
        break;
    }

    return filtered;
  }, [allConferences, matchQuery, selectedSubjects, selectedLocations, selectedStatuses, matchDates, sortBy]);

  /**
   * Facet counts.
   *
   * Each dimension is counted with its OWN selection lifted and every other
   * filter still applied. That's the behaviour that makes a filter list
   * readable: the number beside "Hybrid" tells you what you'd get by adding it,
   * so an option showing 0 is visibly a dead end before you click it.
   *
   * Counted against the active/archive view you are actually looking at —
   * otherwise the sidebar promises conferences that live on the other tab.
   */
  const facetCounts = useMemo(() => {
    const pool = allConferences?.filter(
      (c) => matchQuery(c) && matchDates(c) && isPastConference(c) === showArchive
    ) || [];

    const tally = (ids, testOne, otherDims) =>
      ids.reduce((acc, id) => {
        acc[id] = pool.filter((c) => otherDims(c) && testOne(c, [id])).length;
        return acc;
      }, {});

    return {
      subjects: tally(
        ['medicine', 'ai', 'business', 'engineering', 'science'],
        matchSubject,
        (c) => matchLocation(c, selectedLocations) && matchStatus(c, selectedStatuses)
      ),
      locations: tally(
        ['virtual', 'hybrid', 'in-person'],
        matchLocation,
        (c) => matchSubject(c, selectedSubjects) && matchStatus(c, selectedStatuses)
      ),
      statuses: tally(
        ['open', 'closing-soon', 'closed'],
        matchStatus,
        (c) => matchSubject(c, selectedSubjects) && matchLocation(c, selectedLocations)
      ),
    };
  }, [allConferences, matchQuery, matchDates, showArchive, selectedSubjects, selectedLocations, selectedStatuses]);

  useEffect(() => {
    if (loading) return;

    setFilteredConferences(filterConferences());
    setVisibleCount(PAGE_SIZE);
  }, [filterConferences, loading]);

  const handleSubjectToggle = (subjectId) => {
    setSelectedSubjects((prev) =>
    prev?.includes(subjectId) ?
    prev?.filter((id) => id !== subjectId) :
    [...prev, subjectId]
    );
  };

  const handleLocationToggle = (locationId) => {
    setSelectedLocations((prev) =>
    prev?.includes(locationId) ?
    prev?.filter((id) => id !== locationId) :
    [...prev, locationId]
    );
  };

  const handleStatusToggle = (statusId) => {
    setSelectedStatuses((prev) =>
    prev?.includes(statusId) ?
    prev?.filter((id) => id !== statusId) :
    [...prev, statusId]
    );
  };

  const handleDateRangeChange = (type, value) => {
    setDateRange((prev) => ({ ...prev, [type]: value }));
  };

  const handleClearFilters = () => {
    setSelectedSubjects([]);
    setSelectedLocations([]);
    setSelectedStatuses([]);
    setDateRange({ start: '', end: '' });
    setSortBy('relevance');
    setSearchQuery('');
  };

  const source = filteredConferences || [];
  const activeConferences = source?.filter((conf) => !isPastConference(conf));
  let archivedConferences = source?.filter((conf) => isPastConference(conf));

  // Archive reads best most-recent-first unless the user picked an explicit sort.
  if (sortBy === 'relevance') {
    archivedConferences = [...archivedConferences]?.sort(
      (a, b) =>
        new Date(b?.endDate || b?.startDate) - new Date(a?.endDate || a?.startDate)
    );
  }

  const currentList = showArchive ? archivedConferences : activeConferences;

  /* Active filters, as a flat list the chip row and the drawer badge both read.
     Labels come from the same i18n keys the sidebar uses, so a chip can never
     disagree with the control that set it. Sort is deliberately absent — it is
     not a filter and removing it from a chip row would imply it hides results. */
  const LABELS = {
    subjects: {
      medicine: t('publicBoard.subjects.medicine'), ai: t('publicBoard.subjects.ai'),
      business: t('publicBoard.subjects.business'), engineering: t('publicBoard.subjects.engineering'),
      science: t('publicBoard.subjects.science'),
    },
    locations: {
      virtual: t('publicBoard.locations.virtual'), hybrid: t('publicBoard.locations.hybrid'),
      'in-person': t('publicBoard.locations.inPerson'),
    },
    statuses: {
      open: t('publicBoard.statuses.open'), 'closing-soon': t('publicBoard.statuses.closingSoon'),
      closed: t('publicBoard.statuses.closed'),
    },
  };

  const activeFilterChips = [
    ...(searchQuery?.trim()
      ? [{ key: 'q', label: t('publicBoard.searchLabel', 'Search'), value: searchQuery.trim(), onRemove: () => setSearchQuery('') }]
      : []),
    ...(selectedSubjects || []).map((id) => ({
      key: `subject:${id}`, label: t('publicBoard.subjectArea'),
      value: LABELS.subjects[id] || id, onRemove: () => handleSubjectToggle(id),
    })),
    ...(selectedLocations || []).map((id) => ({
      key: `location:${id}`, label: t('publicBoard.locationType'),
      value: LABELS.locations[id] || id, onRemove: () => handleLocationToggle(id),
    })),
    ...(selectedStatuses || []).map((id) => ({
      key: `status:${id}`, label: t('publicBoard.submissionStatus'),
      value: LABELS.statuses[id] || id, onRemove: () => handleStatusToggle(id),
    })),
    ...(dateRange?.start ? [{ key: 'from', label: t('publicBoard.startDate'), value: dateRange.start, onRemove: () => handleDateRangeChange('start', '') }] : []),
    ...(dateRange?.end ? [{ key: 'to', label: t('publicBoard.endDate'), value: dateRange.end, onRemove: () => handleDateRangeChange('end', '') }] : []),
  ];
  const activeFilterCount = activeFilterChips.length;

  const handleViewChange = (archive) => {
    setShowArchive(archive);
    setVisibleCount(PAGE_SIZE);
  };

  const searchSuggestions = [];

  const filteredSuggestions = searchQuery?.trim() ?
  searchSuggestions?.filter((suggestion) =>
  suggestion?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  ) :
  [];

  return (
    <div className="min-h-screen bg-background bg-mesh">
      <UniversalNavbar />
      <main className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="page-shell">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground tracking-tight mb-1">
              {t('publicBoard.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('publicBoard.subtitle')}
            </p>
          </div>

          {/* Toolbar: search, sort, and (below lg) the filter drawer trigger.
              Sort lives here and only here. It used to be passed to SearchBar,
              which ignored the props entirely, AND rendered again inside the
              filter sidebar — where it doesn't belong, because sorting never
              changes which conferences you can see. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
            <div className="flex-1 min-w-0">
              <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="conf-sort" className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground whitespace-nowrap">
                {t('publicBoard.sortBy')}
              </label>
              <select
                id="conf-sort"
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="h-9 px-3 bg-card border border-input text-[13px] text-foreground focus-ring"
              >
                <option value="relevance">{t('publicBoard.sort.relevance')}</option>
                <option value="deadline">{t('publicBoard.sort.deadline')}</option>
                <option value="date-asc">{t('publicBoard.sort.dateAsc')}</option>
                <option value="date-desc">{t('publicBoard.sort.dateDesc')}</option>
              </select>
              <Button
                variant="outline"
                size="default"
                className="lg:hidden"
                onClick={() => setIsMobileFilterOpen(true)}
                iconName="Filter"
                iconPosition="left"
              >
                {t('publicBoard.filters')}
                {activeFilterCount > 0 && ` (${activeFilterCount})`}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-5">
            <div className="hidden lg:block">
              <FilterPanel
                selectedSubjects={selectedSubjects}
                selectedLocations={selectedLocations}
                selectedStatuses={selectedStatuses}
                dateRange={dateRange}
                facetCounts={facetCounts}
                onSubjectToggle={handleSubjectToggle}
                onLocationToggle={handleLocationToggle}
                onStatusToggle={handleStatusToggle}
                onDateRangeChange={handleDateRangeChange}
                onClearFilters={handleClearFilters}
              />
            </div>

            <div className="min-w-0">
              {/* §4 tab bar: a 2px rule under the active tab. This was a
                  segmented control with a solid primary fill on the selected
                  half — a pill, which radius 0 has removed everywhere else. */}
              <div className="flex items-center gap-4 border-b border-border mb-4">
                <button
                  type="button"
                  onClick={() => handleViewChange(false)}
                  aria-current={!showArchive ? 'page' : undefined}
                  className={`inline-flex items-center gap-1.5 h-9 -mb-px border-b-2 text-[13px] font-medium transition-colors focus-ring ${
                    !showArchive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t('publicBoard.tabs.active')}
                  {!loading && (
                    <span className="font-mono text-[11px] tabular-nums" dir="ltr">
                      {activeConferences?.length || 0}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleViewChange(true)}
                  aria-current={showArchive ? 'page' : undefined}
                  className={`inline-flex items-center gap-1.5 h-9 -mb-px border-b-2 text-[13px] font-medium transition-colors focus-ring ${
                    showArchive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Archive" size={14} />
                  {t('publicBoard.tabs.archive')}
                  {!loading && (
                    <span className="font-mono text-[11px] tabular-nums" dir="ltr">
                      {archivedConferences?.length || 0}
                    </span>
                  )}
                </button>
              </div>

              {/* What's actually filtered, above the results rather than only
                  inside the sidebar — so someone looking at 3 of 40 rows can
                  see why, and undo one dimension without hunting for it. */}
              <ActiveFilters
                className="mb-4"
                resultCount={currentList?.length || 0}
                totalCount={
                  (showArchive
                    ? allConferences?.filter(isPastConference)
                    : allConferences?.filter((c) => !isPastConference(c))
                  )?.length || 0
                }
                onClearAll={handleClearFilters}
                filters={activeFilterChips}
              />

              {showArchive && archivedConferences?.length > 0 && (
                <p className="mb-4 text-xs text-muted-foreground flex items-center gap-1.5">
                  <Icon name="Info" size={13} />
                  {t('publicBoard.archiveNote')}
                </p>
              )}

              <ConferenceGrid
                conferences={currentList?.slice(0, visibleCount)}
                loading={loading || filteredConferences === null}
                error={error}
                filtered={activeFilterCount > 0}
                onClearFilters={handleClearFilters}
              />

              {!loading && currentList?.length > visibleCount &&
                <div className="mt-8 flex justify-center">
                  <Button
                  variant="outline"
                  size="default"
                  iconName="ChevronDown"
                  iconPosition="right"
                  className="w-full sm:w-auto"
                  onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
                  >
                    {t('publicBoard.loadMore')}
                  </Button>
                </div>
              }
            </div>
          </div>
        </div>
      </main>

      <MobileFilterDrawer
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        selectedSubjects={selectedSubjects}
        selectedLocations={selectedLocations}
        selectedStatuses={selectedStatuses}
        dateRange={dateRange}
        facetCounts={facetCounts}
        onSubjectToggle={handleSubjectToggle}
        onLocationToggle={handleLocationToggle}
        onStatusToggle={handleStatusToggle}
        onDateRangeChange={handleDateRangeChange}
        onClearFilters={handleClearFilters}
      />
    </div>
  );

};

export default PublicConferenceBoard;
